import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, RefreshCw } from 'lucide-react';
import {
  DashboardDataTableColumnChooser,
  DashboardTableMultiSelectFilter,
  DashboardTableSearchInput,
  type DashboardDataTableColumnOption,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { IntegrationStatus, integrationStatusLabel } from '../../model';

export function IntegrationsTableToolbar(props: {
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusValue: IntegrationStatus[];
  statusCounts: Partial<Record<IntegrationStatus, number | undefined>>;
  onStatusChange: (value: IntegrationStatus[]) => void;
  refreshing: boolean;
  onRefresh: () => void;
  canCreate: boolean;
  demandPartnerId?: string | undefined;
  columnOptions: readonly DashboardDataTableColumnOption[];
  hiddenColumnIds: ReadonlySet<string>;
  onToggleColumn: (id: string, visible: boolean) => void;
  onResetColumns: () => void;
  bulkBar?: ReactNode | undefined;
}) {
  const statusLabel = props.statusValue.length
    ? props.statusValue.map(integrationStatusLabel).join(', ')
    : dashboardT('integrations.allStatuses');

  return (
    <>
      {props.bulkBar ?? (
        <>
          <DashboardTableSearchInput
            aria-label={dashboardT('integrations.search')}
            placeholder={dashboardT('integrations.search')}
            value={props.searchValue}
            onChange={(event) => {
              props.onSearchChange(event.target.value);
            }}
          />
          <DashboardTableMultiSelectFilter
            ariaLabel={dashboardT('integrations.statusFilter')}
            label={statusLabel}
            value={props.statusValue}
            options={Object.values(IntegrationStatus).map((status) => ({
              value: status,
              label: integrationStatusLabel(status),
              count: props.statusCounts[status] ?? 0,
            }))}
            onChange={props.onStatusChange}
          />
        </>
      )}
      <DashboardDataTableColumnChooser
        ariaLabel={dashboardT('integrations.columnChooser')}
        columns={props.columnOptions}
        hidden={props.hiddenColumnIds}
        onToggle={props.onToggleColumn}
        onReset={props.onResetColumns}
        resetLabel={dashboardT('integrations.resetColumns')}
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={dashboardT('integrations.refresh')}
        disabled={props.refreshing}
        onClick={props.onRefresh}
      >
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
      </Button>
      {props.canCreate ? (
        <Button asChild>
          <Link
            to="/demand/integrations/create"
            search={{ copyFrom: undefined, demandPartnerId: props.demandPartnerId }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {dashboardT('integrations.new')}
          </Link>
        </Button>
      ) : null}
    </>
  );
}
